import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { getSupabaseFunctionErrorMessage } from "@/lib/supabase-function-errors";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Mail, Trash2, Settings } from "lucide-react";
import { UserDetailsDialog } from "./UserDetailsDialog";

interface WaitlistEntry {
  id: string;
  name: string;
  email: string;
  status: string;
  user_type?: string;
  created_at: string;
  approved_at?: string;
}

interface Profile {
  id: string;
  user_id: string;
  email: string;
  name?: string;
  created_at: string;
}

interface UserWithRole extends Profile {
  is_admin: boolean;
  is_author: boolean;
  is_anonymous: boolean;
  credits?: string | null;
}

interface AdminUserResponse {
  user?: Partial<UserWithRole> & { user_id: string };
  error?: string;
}

const getErrorMessage = (error: unknown, fallback: string) => {
  if (error instanceof Error && error.message) return error.message;
  if (typeof error === "object" && error && "message" in error) {
    const message = (error as { message?: unknown }).message;
    if (typeof message === "string" && message) return message;
  }
  return fallback;
};

export const UserManagement = () => {
  const [waitlist, setWaitlist] = useState<WaitlistEntry[]>([]);
  const [users, setUsers] = useState<UserWithRole[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<string>("users");
  const [detailsUser, setDetailsUser] = useState<UserWithRole | null>(null);
  const { toast } = useToast();

  const fetchData = useCallback(async (showLoader: boolean = false) => {
    if (showLoader) setLoading(true);
    
    // Fetch waitlist
    const { data: waitlistData, error: waitlistError } = await supabase
      .from('waitlist')
      .select('*')
      .neq('status', 'registered')
      .order('created_at', { ascending: false });

    if (waitlistError) {
      toast({
        title: "Error",
        description: "Failed to fetch waitlist",
        variant: "destructive",
      });
    } else {
      setWaitlist(waitlistData || []);
    }

    // Fetch users with their roles
    const { data: usersData, error: usersError } = await supabase
      .from('profiles')
      .select('*')
      .order('created_at', { ascending: false });

    if (usersError) {
      toast({
        title: "Error",
        description: "Failed to fetch users",
        variant: "destructive",
      });
    } else {
      // Fetch credits attribute for all users in one query
      const userIds = (usersData || []).map((u) => u.user_id);
      const creditsMap = new Map<string, string>();
      if (userIds.length > 0) {
        const { data: attrs } = await supabase
          .from('profile_attributes')
          .select('user_id, attribute_value')
          .eq('attribute_name', 'credits')
          .in('user_id', userIds);
        (attrs || []).forEach((a) => creditsMap.set(a.user_id, a.attribute_value));
      }

      const usersWithRoles: UserWithRole[] = (usersData || []).map(user => ({
        ...user,
        is_admin: user.is_admin || false,
        is_author: user.is_author || false,
        is_anonymous: user.is_anonymous || false,
        credits: creditsMap.get(user.user_id) ?? null,
      }));

      setUsers(usersWithRoles);
    }

    setLoading(false);
  }, [toast]);

  useEffect(() => {
    fetchData(true);
  }, [fetchData]);

  const handleApprove = async (waitlistId: string) => {
    try {
      const { error } = await supabase.functions.invoke(`approve-waitlist?id=${waitlistId}`);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Invitation email sent!",
      });

      fetchData();
    } catch (error: unknown) {
      toast({
        title: "Error",
        description: getErrorMessage(error, "Failed to approve user"),
        variant: "destructive",
      });
    }
  };

  const handleDelete = async (waitlistId: string) => {
    try {
      const { error } = await supabase
        .from('waitlist')
        .delete()
        .eq('id', waitlistId);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Entry deleted",
      });

      fetchData();
    } catch (error: unknown) {
      toast({
        title: "Error",
        description: getErrorMessage(error, "Failed to delete entry"),
        variant: "destructive",
      });
    }
  };

  const handleDeleteUser = async (userId: string, email?: string, skipConfirm = false) => {
    if (!skipConfirm && !confirm('Are you sure you want to delete this user? This action cannot be undone.')) {
      return;
    }

    try {
      const { error, data } = await supabase.functions.invoke('delete-user', {
        body: { userId, email }
      });

      if (error) throw error;

      toast({
        title: data?.status === "done" ? "User removed" : "Deletion queued",
        description: (data && (data.message || data.details)) || "User deleted successfully",
      });

      fetchData();
    } catch (error: unknown) {
      toast({
        title: "Error",
        description: await getSupabaseFunctionErrorMessage(error, "Failed to delete user"),
        variant: "destructive",
      });
    }
  };

  const mergeUser = (user: UserWithRole, updatedUser: AdminUserResponse["user"]): UserWithRole => ({
    ...user,
    ...updatedUser,
    is_admin: updatedUser?.is_admin ?? user.is_admin,
    is_author: updatedUser?.is_author ?? user.is_author,
    is_anonymous: updatedUser?.is_anonymous ?? user.is_anonymous,
  });

  const applyUpdatedUser = (updatedUser: AdminUserResponse["user"]) => {
    if (!updatedUser?.user_id) return;

    setUsers((currentUsers) => currentUsers.map((user) => (
      user.user_id === updatedUser.user_id ? mergeUser(user, updatedUser) : user
    )));
  };

  const applyUpdatedAttribute = (
    userId: string,
    attributeName: string,
    attributeValue: string
  ) => {
    if (attributeName !== 'credits') return;

    const credits = attributeValue.trim() === '' ? null : attributeValue;
    const patch = { credits };

    setUsers((currentUsers) => currentUsers.map((user) => (
      user.user_id === userId ? { ...user, ...patch } : user
    )));
    setDetailsUser((currentUser) => (
      currentUser?.user_id === userId ? { ...currentUser, ...patch } : currentUser
    ));
  };

  const handleToggleAdmin = async (userId: string, currentStatus: boolean) => {
    try {
      const nextStatus = !currentStatus;
      const { data, error } = await supabase.functions.invoke<AdminUserResponse>('admin-users', {
        body: { action: 'set_admin', userId, isAdmin: nextStatus },
      });

      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      applyUpdatedUser(data?.user);

      toast({
        title: "Success",
        description: `Admin status ${nextStatus ? 'granted' : 'revoked'}`,
      });

      return data?.user ?? null;
    } catch (error: unknown) {
      toast({
        title: "Error",
        description: getErrorMessage(error, "Failed to update admin status"),
        variant: "destructive",
      });
      return null;
    }
  };

  const handleToggleExpert = async (userId: string, currentStatus: boolean) => {
    try {
      const nextStatus = !currentStatus;
      const { data, error } = await supabase.functions.invoke<AdminUserResponse>('admin-users', {
        body: { action: 'set_author', userId, isAuthor: nextStatus },
      });

      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      applyUpdatedUser(data?.user);

      toast({
        title: "Success",
        description: `Expert status ${nextStatus ? 'granted' : 'revoked'}`,
      });

      return data?.user ?? null;
    } catch (error: unknown) {
      toast({
        title: "Error",
        description: getErrorMessage(error, "Failed to update expert status"),
        variant: "destructive",
      });
      return null;
    }
  };

  const getStatusBadge = (status: string) => {
    if (status === 'waiting') {
      return <Badge variant="secondary">Waiting</Badge>;
    } else if (status === 'registered') {
      return <Badge variant="default">Registered</Badge>;
    } else if (status.startsWith('approved')) {
      return <Badge className="bg-green-500">Approved</Badge>;
    }
    return <Badge>{status}</Badge>;
  };

  if (loading) {
    return <div className="text-center p-8">Loading...</div>;
  }

  const registeredUsers = users.filter((u) => !u.is_anonymous);
  const guestUsers = users.filter((u) => u.is_anonymous);

  const renderUsersTable = (rows: UserWithRole[], title: string) => (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Admin</TableHead>
              <TableHead>Expert</TableHead>
              <TableHead>Credits</TableHead>
              <TableHead>Joined</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((user) => (
              <TableRow key={user.id}>
                <TableCell>{user.name || 'N/A'}</TableCell>
                <TableCell>{user.email}</TableCell>
                <TableCell>
                  {user.is_admin ? (
                    <Badge variant="default">Admin</Badge>
                  ) : (
                    <Badge variant="secondary">User</Badge>
                  )}
                </TableCell>
                <TableCell>
                  {user.is_author ? (
                    <Badge variant="default" className="bg-blue-500">Expert</Badge>
                  ) : (
                    <Badge variant="secondary">User</Badge>
                  )}
                </TableCell>
                <TableCell>
                  <span className="font-mono text-sm">{user.credits ?? '—'}</span>
                </TableCell>
                <TableCell>
                  {new Date(user.created_at).toLocaleDateString()}
                </TableCell>
                <TableCell>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setDetailsUser(user)}
                      className="gap-2"
                    >
                      <Settings className="w-4 h-4" />
                      Details
                    </Button>
                    {user.is_anonymous && (
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => handleDeleteUser(user.user_id, user.email, true)}
                        className="gap-2"
                      >
                        <Trash2 className="w-4 h-4" />
                        Delete
                      </Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );

  return (
    <div className="space-y-6">
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="users">
            Users ({registeredUsers.length})
          </TabsTrigger>
          <TabsTrigger value="guests">
            Guests ({guestUsers.length})
          </TabsTrigger>
          <TabsTrigger value="waitlist">
            Applications ({waitlist.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="waitlist" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Beta Applications</CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Submitted</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {waitlist.map((entry) => (
                    <TableRow key={entry.id}>
                      <TableCell>{entry.name}</TableCell>
                      <TableCell>{entry.email}</TableCell>
                      <TableCell>
                        <Badge variant={(entry.user_type || 'user') === 'expert' ? 'default' : 'secondary'}>
                          {(entry.user_type || 'user') === 'expert' ? '🎓 Expert' : '👤 User'}
                        </Badge>
                      </TableCell>
                      <TableCell>{getStatusBadge(entry.status)}</TableCell>
                      <TableCell>
                        {new Date(entry.created_at).toLocaleDateString()}
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-2">
                          {(entry.status === 'waiting' || entry.status === 'approved') && (
                            <Button
                              size="sm"
                              onClick={() => handleApprove(entry.id)}
                              className="gap-2"
                            >
                              <Mail className="w-4 h-4" />
                              {entry.status === 'waiting' ? 'Approve' : 'Resend invite'}
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() => handleDelete(entry.id)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="users" className="space-y-4">
          {renderUsersTable(registeredUsers, 'Registered Users')}
        </TabsContent>

        <TabsContent value="guests" className="space-y-4">
          {renderUsersTable(guestUsers, 'Guest Users')}
        </TabsContent>
      </Tabs>

      <UserDetailsDialog
        userId={detailsUser?.user_id ?? null}
        email={detailsUser?.email}
        name={detailsUser?.name ?? undefined}
        isAdmin={detailsUser?.is_admin ?? false}
        isExpert={detailsUser?.is_author ?? false}
        onToggleAdmin={async () => {
          if (!detailsUser) return;
          const updatedUser = await handleToggleAdmin(detailsUser.user_id, detailsUser.is_admin);
          if (updatedUser) setDetailsUser((current) => current ? mergeUser(current, updatedUser) : current);
        }}
        onToggleExpert={async () => {
          if (!detailsUser) return;
          const updatedUser = await handleToggleExpert(detailsUser.user_id, detailsUser.is_author);
          if (updatedUser) setDetailsUser((current) => current ? mergeUser(current, updatedUser) : current);
        }}
        onAttributeSaved={applyUpdatedAttribute}
        onDelete={async () => {
          if (!detailsUser) return;
          await handleDeleteUser(detailsUser.user_id, detailsUser.email);
          setDetailsUser(null);
        }}
        open={!!detailsUser}
        onOpenChange={(open) => { if (!open) setDetailsUser(null); }}
      />
    </div>
  );
};
